import React, { useContext } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import QuizEditorContext from "../../../contexts/QuizEditorContext";
import LanguageContext from "../../../contexts/LanguageContext";
import QuizQuestionPreview from "./QuizQuestionPreview";
import QuestionTranslationRow from "./QuestionTranslationRow";
import { makeStyles, Box, Typography, Button } from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  listContainer: {
    padding: "1rem 0",
    width: "100%",
  },
  translationContainer: {
    padding: "0 1rem 4rem 1rem", 
    maxWidth: "100%",
    margin: "0 auto",
    width: "100%",
  },
  infoBox: {
    marginBottom: "2rem",
    textAlign: "center",
  },
}));

export default function QuizEditor({
  quizMode,
  translationMode,
  languageToEditId,
  onEditQuestion,
  onAddQuestion,
  onDeleteQuestion, 
  onSave,
}) {
  const classes = useStyles();
  const { questions, setQuestions } = useContext(QuizEditorContext);
  const { languageInfo } = useContext(LanguageContext);

  const getLanguageName = () => {
    if (!languageInfo || !languageInfo.languages) return "Translation";
    const lang = languageInfo.languages.find(l => l.id === languageToEditId);
    return lang ? lang.displayName : "Translation";
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(questions);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    const updatedItems = items.map((item, idx) => ({ ...item, order: idx }));
    setQuestions(updatedItems);
  };

  const handleTranslationUpdate = (updatedQuestion) => {
    const updatedList = questions.map((q) =>
      q.id === updatedQuestion.id ? updatedQuestion : q
    );
    setQuestions(updatedList);
  };

  // FIX: Disable delete button if only 1 question remains
  const isDeleteDisabled = questions.length <= 1;

  if (translationMode) {
    const targetLanguage = getLanguageName();

    return (
      <div className={classes.translationContainer}>
        {questions.length === 0 ? (
          <Typography align="center" style={{marginTop: '4rem'}}>
            No questions to translate. Add questions in English first.
          </Typography>
        ) : (
          questions.map((question, index) => (
            <QuestionTranslationRow
              key={question.id || index}
              index={index}
              question={question}
              languageId={languageToEditId}
              targetLanguageName={targetLanguage}
              onUpdate={handleTranslationUpdate}
            />
          ))
        )}
      </div>
    );
  }

  return (
    <div className={classes.listContainer}>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="quiz-questions">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              style={{ width: "100%" }}
            >
              {questions.map((question, index) => (
                <Draggable
                  key={question.id || index}
                  draggableId={String(question.id)}
                  index={index}
                >
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={{
                        marginBottom: "1rem",
                        ...provided.draggableProps.style,
                      }}
                    >
                      <QuizQuestionPreview
                        question={question}
                        onEditClick={() => onEditQuestion(question)}
                        onDeleteClick={() => onDeleteQuestion(question)}
                        isDeleteDisabled={isDeleteDisabled} // Pass prop
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <Box mt={2} display="flex" justifyContent="center">
        <Button
          variant="contained"
          color="primary"
          onClick={onAddQuestion}
        >
          + Add New Question
        </Button>
      </Box>
    </div>
  );
}